<script setup>
import { reactive, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { CLOUD_STORAGE } from "../../../core/constants.js";
import CloudStorageService from "../../services/cloudStorageService.js";
import { useDocumentUpload } from "../../composables/useDocumentUpload.js";

const props = defineProps({
  // { id, name, provider }
  storage: { type: Object, required: true },
  // null/undefined = unfiled ("Documents"), matching the local-file upload path
  projectId: { type: String, default: null },
});
const emit = defineEmits(["close", "rename", "remove", "uploaded"]);

const cloudStorageService = new CloudStorageService();
const { uploadCloudDocuments } = useDocumentUpload();

const EXPORTABLE_STATUS = { EXCEEDS_MAXIMUM_SIZE: "EXCEEDS_MAXIMUM_SIZE" };

function close() { emit("close"); }
function onEsc(e) { if (e.key === "Escape") close(); }
onMounted(() => document.addEventListener("keydown", onEsc));
onBeforeUnmount(() => document.removeEventListener("keydown", onEsc));

// ─── Folder navigation stack ────────────────────────────────────────────────
// Each visited folder keeps its own paged generator + accumulated items, so
// going back doesn't lose scroll position or re-fetch what's already loaded.
const viewMode = ref(localStorage.getItem("cloud-finder-view-style") || "list");
watch(viewMode, (val) => localStorage.setItem("cloud-finder-view-style", val));

const state = reactive({
  selectedFiles: new Set(),
  folderItems: [],
  fetching: false,
  uploading: false,
});

class FinderStack {
  #back = [];
  #forward = [];

  get canBack() { return this.#back.length > 1; }
  get canForward() { return this.#forward.length > 0; }
  get currentFolder() { return this.#back[this.#back.length - 1]; }

  get path() {
    const path = this.#back
      .map((item, index, array) => ({ backSteps: array.length - index - 1, name: item.name }))
      .filter((folder) => !!folder.name);
    if (path.length > 4) path.splice(1, path.length - 3, { backSteps: 0, name: "…" });
    return path;
  }

  goBack(times = 1) {
    state.selectedFiles.clear();
    for (let i = 0; i < times; i++) this.#forward.push(this.#back.pop());
    this.#recalc();
  }

  goForward() {
    state.selectedFiles.clear();
    this.#back.push(this.#forward.shift());
    this.#recalc();
  }

  goHome() {
    state.selectedFiles.clear();
    this.#forward = this.#back.splice(1).concat(this.#forward);
    this.#recalc();
  }

  push(id, name, generator) {
    this.#forward = [];
    this.#back.push({ id, name, items: [], generator });
    this.#recalc();
    return this.currentFolder;
  }

  fillFolder(folderId, chunk) {
    const folder = this.#back.find((f) => f.id === folderId);
    if (!folder) return;
    folder.items = folder.items.concat(chunk.map((item) => ({
      ...item,
      isFolder: item.__typename === "CloudStorageFolder" || item.mimeType === "application/vnd.google-apps.shortcut",
      isNotAccessible: item.exportableStatus === EXPORTABLE_STATUS.EXCEEDS_MAXIMUM_SIZE,
    })));
    this.#recalc();
  }

  getFolderById(id) { return this.#back.find((f) => f.id === id); }

  #recalc() { state.folderItems = this.currentFolder?.items ?? []; }
}
const finderStack = new FinderStack();

function getItems(parentFolderId, parentFolderName) {
  let folder = finderStack.getFolderById(parentFolderId);
  if (!folder) {
    const generator = cloudStorageService.folderItems(props.storage.id, parentFolderId);
    folder = finderStack.push(parentFolderId, parentFolderName, generator);
  }
  state.fetching = true;
  folder.generator.next()
    .then((chunk) => { if (chunk.value) finderStack.fillFolder(parentFolderId, chunk.value); })
    .catch((error) => console.error("Error fetching drive items:", error))
    .finally(() => { state.fetching = false; });
}

const listContainerRef = ref(null);
const gridContainerRef = ref(null);
function scrollHandler(e) {
  const { scrollHeight, scrollTop, clientHeight } = e.target;
  if (Math.round(scrollHeight) - Math.round(scrollTop + clientHeight) < 2) {
    getItems(finderStack.currentFolder.id, finderStack.currentFolder.name);
  }
}

onMounted(() => getItems(null, ""));

function itemOnClick(item) {
  if (item.isNotAccessible) return;
  if (item.isFolder) {
    state.selectedFiles.clear();
    getItems(item.id, item.name);
  } else if (state.selectedFiles.has(item)) {
    state.selectedFiles.delete(item);
  } else {
    state.selectedFiles.add(item);
  }
}

function startUpload() {
  const files = Array.from(state.selectedFiles).map((f) => ({ id: f.id, name: f.name }));
  state.uploading = true;
  uploadCloudDocuments(props.projectId, props.storage.id, files);
  state.uploading = false;
  state.selectedFiles.clear();
  // Close the whole Add Document popup (not just this finder) — the upload
  // itself continues in the background via the global upload monitor, same
  // as the local-file path (AddModal.vue's onFilesPicked() does the same).
  emit("uploaded");
}

const showMenu = ref(false);
</script>

<template>
  <div class="fixed inset-0 bg-black/20 dark:bg-black/50 z-[450] flex items-center justify-center p-4" @click.self="close" @click="showMenu = false">
    <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-[720px] flex flex-col overflow-hidden" @click.stop>

      <!-- Header -->
      <div class="flex items-center gap-3 px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div class="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-50 dark:bg-slate-800 shrink-0">
          <svg v-if="storage.provider === CLOUD_STORAGE.GOOGLE_DRIVE" viewBox="0 0 16 16" width="20" height="20" fill="none">
            <path d="M1.20938 13.1039L1.91563 14.3227C2.0625 14.5789 2.27188 14.782 2.52188 14.9289L5.04063 10.5664H0C0 10.8508 0.071875 11.1352 0.21875 11.3914L1.20938 13.1039Z" fill="#0066DA"/>
            <path d="M8 5.43281L5.48125 1.07031C5.23438 1.21719 5.02187 1.41719 4.875 1.67656L0.21875 9.73906C0.075 9.98906 0 10.2734 0 10.5641H5.04063L8 5.43281Z" fill="#00AC47"/>
            <path d="M13.4809 14.9289C13.7277 14.782 13.9402 14.582 14.0871 14.3227L14.3809 13.8195L15.784 11.3914C15.9309 11.1352 16.0027 10.8508 16.0027 10.5664H10.959L12.0309 12.6727L13.4809 14.9289Z" fill="#EA4335"/>
            <path d="M7.99922 5.43281L10.518 1.07031C10.2711 0.923437 9.98672 0.851562 9.69297 0.851562H6.30547C6.01172 0.851562 5.72734 0.932812 5.48047 1.07031L7.99922 5.43281Z" fill="#00832D"/>
            <path d="M10.9602 10.5664H5.04141L2.51953 14.9289C2.76641 15.0758 3.05078 15.1477 3.34453 15.1477H12.657C12.9508 15.1477 13.2352 15.0664 13.482 14.9289L10.9602 10.5664Z" fill="#2684FC"/>
            <path d="M13.4531 5.70781L11.125 1.67656C10.9781 1.42031 10.7687 1.21719 10.5187 1.07031L8 5.43281L10.9594 10.5641H15.9906C15.9906 10.2797 15.9187 9.99531 15.7719 9.73906L13.4531 5.70781Z" fill="#FFBA00"/>
          </svg>
          <svg v-else-if="storage.provider === CLOUD_STORAGE.ONE_DRIVE" viewBox="0 0 16 11" width="22" height="16" fill="none">
            <path d="M6.09859 3.05601L9.45698 5.21557L11.4564 4.31017C11.8625 4.12238 12.2999 4.02513 12.7435 4.02513C12.8185 4.02513 12.8904 4.02849 12.9622 4.03519C12.2812 1.16472 9.55695 -0.565606 6.88273 0.168778C5.67996 0.50076 4.63339 1.30221 3.94922 2.41552H3.9992C4.73961 2.41552 5.46752 2.63685 6.09859 3.05601Z" fill="#0364B8"/>
            <path d="M6.09854 3.05455C5.46748 2.63538 4.73957 2.41406 3.99916 2.41406H3.94917C1.74045 2.44424 -0.027786 4.38919 0.000330719 6.76001C0.00970297 7.61846 0.25963 8.4568 0.71887 9.16101L3.6805 7.82302L4.99574 7.22948L7.92613 5.9049L9.45694 5.21411L6.09854 3.05455Z" fill="#0078D4"/>
            <path d="M12.9662 4.0335C12.8943 4.02679 12.8193 4.02344 12.7475 4.02344C12.3039 4.02344 11.8665 4.12068 11.4604 4.30847L9.46094 5.21388L10.042 5.5861L11.9446 6.81007L12.7756 7.34326L15.6123 9.16748C16.462 7.47068 15.8716 5.35807 14.2939 4.44261C13.8784 4.20452 13.4285 4.06703 12.9662 4.0335Z" fill="#1490DF"/>
            <path d="M12.7683 7.34422L11.9373 6.81104L10.0348 5.58707L9.45369 5.21484L7.92289 5.90563L4.9925 7.23021L3.67726 7.82375L0.71875 9.16174C1.46541 10.3153 2.69005 10.9994 3.99904 10.9994H12.7465C13.9399 10.9994 15.0396 10.2952 15.6081 9.16845L12.7683 7.34422Z" fill="#28A8EA"/>
          </svg>
          <svg v-else-if="storage.provider === CLOUD_STORAGE.BOX_DRIVE" viewBox="0 0 16 9" width="22" height="13">
            <path d="M8.59631 2.65621C7.40664 2.65621 6.37363 3.33832 5.85161 4.33963C5.32958 3.33828 4.29661 2.65628 3.10698 2.65628C2.40777 2.65628 1.76254 2.89186 1.24339 3.28947V0.620686H1.2431C1.23584 0.276769 0.960655 0 0.621641 0C0.28259 0 0.00748011 0.276769 0.000287697 0.620686H0V5.8771H0.00129464C0.0289135 7.60259 1.40831 8.99307 3.10694 8.99307C4.29657 8.99307 5.32958 8.31085 5.85157 7.3095C6.37363 8.31092 7.40661 8.993 8.59623 8.993C10.3122 8.993 11.7032 7.57435 11.7032 5.82462C11.7033 4.07467 10.3121 2.65621 8.59631 2.65621ZM3.10694 7.72504C2.07767 7.72504 1.24335 6.87419 1.24335 5.82466C1.24335 4.77497 2.07771 3.92417 3.10694 3.92417C4.13622 3.92417 4.97057 4.77497 4.97057 5.82466C4.97057 6.87419 4.13622 7.72504 3.10694 7.72504ZM8.59631 7.72504C7.567 7.72504 6.73271 6.87419 6.73271 5.82458C6.73271 4.7749 7.56703 3.92417 8.59631 3.92417C9.6255 3.92417 10.4599 4.7749 10.4599 5.82458C10.4599 6.87419 9.6255 7.72504 8.59631 7.72504Z" fill="#0075C9"/>
            <path d="M15.8829 7.99439L14.2917 5.82545L15.8829 3.65648L15.8826 3.65622C16.0794 3.37678 16.0223 2.98698 15.7512 2.77927C15.4802 2.57177 15.0972 2.62447 14.8889 2.89504L14.8887 2.89497L13.5154 4.767L12.1421 2.89497L12.1419 2.8953C11.9335 2.62447 11.5506 2.57177 11.2795 2.77941C11.0083 2.98713 10.9513 3.37692 11.1482 3.6563L11.1479 3.65648L12.7391 5.82545L11.1479 7.99439L11.1482 7.99457C10.9513 8.27401 11.0083 8.66374 11.2795 8.87138C11.5506 9.07909 11.9335 9.02632 12.1419 8.75567L12.1421 8.75593L13.5154 6.88383L14.8887 8.75593L14.8889 8.75586C15.0972 9.02639 15.4802 9.07909 15.7512 8.87152C16.0223 8.66388 16.0794 8.27409 15.8826 7.99468L15.8829 7.99439Z" fill="#0075C9"/>
          </svg>
          <svg v-else viewBox="0 0 16 15" width="20" height="19">
            <path d="M4.71562 0L0 3.07483L3.28437 5.7315L8 2.773L4.71562 0ZM0 8.31587L4.71562 11.4284L8 8.65542L3.28437 5.7315L0 8.31587ZM8 8.65542L11.3219 11.4284L16 8.35359L12.7563 5.73465C12.7563 5.7315 8 8.65542 8 8.65542ZM16 3.07483L11.3219 0L8 2.773L12.7563 5.73465L16 3.07483ZM8.0375 9.26535L4.71562 12.0384L3.31875 11.0889V12.1515L8.03438 15L12.75 12.1515V11.0889L11.3156 12.0384C11.3219 12.0352 8.0375 9.26535 8.0375 9.26535Z" fill="#007EE5"/>
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <h2 class="text-base font-bold text-slate-800 dark:text-slate-100 m-0 truncate">{{ storage.name }}</h2>
          <p class="text-xs text-slate-400 dark:text-slate-500 m-0">Select documents to add. Maximum file size: 200MB</p>
        </div>
        <div class="relative shrink-0">
          <button
            class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
            @click.stop="showMenu = !showMenu"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
          <div
            v-if="showMenu"
            class="absolute top-[calc(100%+4px)] right-0 w-36 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl py-1.5 z-10 overflow-hidden"
            @click.stop
          >
            <button class="flex items-center w-full px-3 py-2 text-sm text-left border-0 bg-transparent cursor-pointer text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" @click="showMenu = false; emit('rename')">
              Rename
            </button>
            <button class="flex items-center w-full px-3 py-2 text-sm text-left border-0 bg-transparent cursor-pointer text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" @click="showMenu = false; emit('remove')">
              Remove
            </button>
          </div>
        </div>
        <button
          class="flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors shrink-0"
          @click="close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <!-- Navigator -->
      <div class="flex items-center justify-between gap-3 px-4 py-2 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div class="flex items-center gap-1 min-w-0 flex-1">
          <button
            class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border-0 bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-default shrink-0"
            :disabled="!finderStack.canBack"
            @click="finderStack.goBack()"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
          <button
            class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 border-0 bg-transparent cursor-pointer disabled:opacity-30 disabled:cursor-default shrink-0"
            :disabled="!finderStack.canForward"
            @click="finderStack.goForward()"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </button>
          <div v-if="finderStack.path.length" class="flex items-center gap-1 min-w-0 overflow-hidden ml-1">
            <button
              v-for="folder in finderStack.path"
              :key="folder.name + folder.backSteps"
              class="max-w-[130px] truncate text-sm border-0 bg-transparent cursor-pointer px-1 py-1 rounded"
              :class="folder.backSteps === 0
                ? 'text-slate-800 dark:text-slate-100 font-semibold cursor-default'
                : 'text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'"
              @click="folder.backSteps > 0 && finderStack.goBack(folder.backSteps)"
            >
              {{ folder.name }}
            </button>
          </div>
        </div>

        <!-- View mode toggle -->
        <div class="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5 gap-0.5 shrink-0">
          <button
            :class="['flex items-center justify-center w-7 h-7 rounded-md transition-colors border-0 cursor-pointer',
              viewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-700 dark:text-slate-200' : 'bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300']"
            title="List view"
            @click="viewMode = 'list'"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
          </button>
          <button
            :class="['flex items-center justify-center w-7 h-7 rounded-md transition-colors border-0 cursor-pointer',
              viewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-700 dark:text-slate-200' : 'bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300']"
            title="Grid view"
            @click="viewMode = 'grid'"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
              <rect x="13" y="3" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
              <rect x="3" y="13" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
              <rect x="13" y="13" width="8" height="8" rx="1" stroke="currentColor" stroke-width="2" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="relative min-h-[360px] max-h-[360px] bg-slate-50 dark:bg-slate-950/40">
        <div v-if="state.fetching && !state.folderItems.length" class="absolute inset-0 flex items-center justify-center">
          <div class="w-6 h-6 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-brand animate-spin" />
        </div>

        <div v-else-if="!state.folderItems.length" class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>
          <p class="text-sm m-0">This folder is empty</p>
        </div>

        <!-- List view -->
        <div v-else-if="viewMode === 'list'" ref="listContainerRef" class="h-[360px] overflow-y-auto" @scroll="scrollHandler">
          <div
            v-for="item in state.folderItems"
            :key="item.id"
            class="flex items-center gap-3 px-5 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0 cursor-pointer transition-colors"
            :class="item.isNotAccessible
              ? 'opacity-50 cursor-not-allowed'
              : (state.selectedFiles.has(item) ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-white dark:hover:bg-slate-800/60')"
            @click="itemOnClick(item)"
          >
            <svg v-if="item.isFolder" width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-slate-400 shrink-0"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" class="text-slate-400 shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <div class="flex-1 min-w-0">
              <p class="text-sm text-slate-700 dark:text-slate-200 m-0 truncate">{{ item.name }}</p>
              <p v-if="item.exportableStatus === EXPORTABLE_STATUS.EXCEEDS_MAXIMUM_SIZE" class="text-xs italic text-slate-400 dark:text-slate-500 m-0">File exceeds maximum size allowed</p>
            </div>
            <svg v-if="state.selectedFiles.has(item)" width="18" height="18" viewBox="0 0 24 24" fill="none" class="text-blue-500 shrink-0"><circle cx="12" cy="12" r="10" fill="currentColor" /><path d="M7 12.5l3 3 7-7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          </div>
        </div>

        <!-- Grid view -->
        <div v-else ref="gridContainerRef" class="h-[360px] overflow-y-auto p-4 flex flex-wrap gap-3 content-start" @scroll="scrollHandler">
          <div
            v-for="item in state.folderItems"
            :key="item.id"
            class="w-[110px] flex flex-col items-center gap-2 p-2 rounded-xl cursor-pointer transition-colors"
            :class="item.isNotAccessible ? 'opacity-50 cursor-not-allowed' : (state.selectedFiles.has(item) ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-white dark:hover:bg-slate-800/60')"
            @click="itemOnClick(item)"
          >
            <svg v-if="item.isFolder" width="40" height="40" viewBox="0 0 24 24" fill="none" class="text-slate-300 dark:text-slate-600 shrink-0"><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" /></svg>
            <svg v-else width="34" height="34" viewBox="0 0 24 24" fill="none" class="text-slate-300 dark:text-slate-600 shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /><path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <p class="text-xs text-slate-600 dark:text-slate-300 m-0 text-center truncate w-full">{{ item.name }}</p>
          </div>
        </div>

        <div v-if="state.fetching && state.folderItems.length" class="absolute bottom-2 left-1/2 -translate-x-1/2">
          <div class="w-5 h-5 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-brand animate-spin" />
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0">
        <button
          class="px-5 py-2.5 border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
          @click="close"
        >
          Cancel
        </button>
        <button
          :disabled="state.selectedFiles.size === 0 || state.uploading"
          class="px-5 py-2.5 bg-slate-800 dark:bg-slate-700 text-white rounded-xl text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default"
          @click="startUpload"
        >
          Upload{{ state.selectedFiles.size ? ` (${state.selectedFiles.size})` : "" }}
        </button>
      </div>
    </div>
  </div>
</template>
